import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, TrendingUp, DollarSign, Calendar,
  LogOut, RefreshCw, Camera, MapPin, Mail,
  ChevronDown, ChevronUp, AlertCircle, Lock,
  Download, KeyRound, Check, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEALER_COST = 60;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_camera-site-profit/artifacts/zi4nnloe_White%20logo%20-%20no%20background.png";

const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ── CSV Export ────────────────────────────────────────────────────────────────
function exportCSV(leads) {
  const headers = ["Name", "Company", "Email", "Sites", "Cameras/Site", "Total Cameras", "Sell Price ($/cam/mo)", "Monthly RMR ($)", "Monthly Earnings ($)", "Date Submitted"];
  const rows = leads.map((l) => {
    const totalCams = (l.sites || 0) * (l.cameras || 0);
    const rmr = totalCams * (l.sell_price || 0);
    const earnings = totalCams * ((l.sell_price || 0) - DEALER_COST);
    return [
      l.name || "",
      l.company || "",
      l.email || "",
      l.sites ?? 0,
      l.cameras ?? 0,
      totalCams,
      l.sell_price ?? 0,
      rmr,
      earnings,
      l.timestamp ? new Date(l.timestamp).toLocaleDateString("en-CA") : "—",
    ];
  });
  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `247alert-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Change Password Modal ─────────────────────────────────────────────────────
function ChangePasswordModal({ token, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (next !== confirm) { setError("New passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setTimeout(onClose, 1600);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" data-testid="change-pw-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" data-testid="change-pw-modal" style={{ maxWidth: 400 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginBottom: 20 }}>
          <img src={LOGO_URL} alt="247 Alert" className="admin-nav-logo-img" style={{ height: 36 }} />
          <button onClick={onClose} className="admin-refresh-btn" data-testid="close-pw-modal" style={{ padding: "6px 8px" }}>
            <X size={14} />
          </button>
        </div>

        <div className="modal-lock-icon" style={{ marginBottom: 14 }}>
          <KeyRound size={24} color="#9e1c1c" />
        </div>
        <h2 className="modal-title" style={{ marginBottom: 6 }}>Change Password</h2>
        <p className="modal-sub" style={{ marginBottom: 24 }}>Update your admin password below.</p>

        {success ? (
          <div className="pw-success" data-testid="pw-success">
            <Check size={20} color="#22c55e" />
            <span>Password updated successfully!</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-form" data-testid="change-pw-form">
            {error && (
              <div className="admin-error-banner" data-testid="pw-error">
                <AlertCircle size={13} /><span>{error}</span>
              </div>
            )}
            <div>
              <label className="pw-field-label">Current Password</label>
              <div className="modal-input-wrap">
                <Lock size={13} className="modal-input-icon" />
                <input data-testid="current-pw-input" type="password" className="modal-input" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className="pw-field-label">New Password</label>
              <div className="modal-input-wrap">
                <Lock size={13} className="modal-input-icon" />
                <input data-testid="new-pw-input" type="password" className="modal-input" placeholder="Min. 6 characters" value={next} onChange={(e) => setNext(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className="pw-field-label">Confirm New Password</label>
              <div className="modal-input-wrap">
                <Lock size={13} className="modal-input-icon" />
                <input data-testid="confirm-pw-input" type="password" className="modal-input" placeholder="Repeat new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
            </div>
            <button type="submit" className="modal-btn" data-testid="save-pw-btn" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Admin Login ───────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="bg-texture" />
      <div className="admin-login-box" data-testid="admin-login-box">
        <img src={LOGO_URL} alt="247 Alert" className="admin-nav-logo-img" />
        <div className="modal-lock-icon" style={{ marginBottom: 16 }}>
          <Lock size={26} color="#9e1c1c" />
        </div>
        <h2 className="modal-title" style={{ marginBottom: 8 }}>Admin Access</h2>
        <p className="modal-sub" style={{ marginBottom: 24 }}>Sign in to view dealer leads</p>
        {error && (
          <div className="admin-error-banner" data-testid="login-error">
            <AlertCircle size={14} /><span>{error}</span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="modal-form" data-testid="admin-login-form">
          <div className="modal-input-wrap">
            <Mail size={14} className="modal-input-icon" />
            <input data-testid="admin-email-input" type="email" className="modal-input" placeholder="admin@247alert.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="modal-input-wrap">
            <Lock size={14} className="modal-input-icon" />
            <input data-testid="admin-password-input" type="password" className="modal-input" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button type="submit" className="modal-btn" data-testid="admin-login-btn" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-header">
        <Icon size={14} color="#9e1c1c" />
        <span className="admin-stat-label">{label}</span>
      </div>
      <div className="admin-stat-value" style={accent ? { color: "#FF4D4D" } : {}}>{value}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

// ── Leads Table ───────────────────────────────────────────────────────────────
function LeadsTable({ leads }) {
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const filtered = leads
    .filter((l) => {
      const q = search.toLowerCase();
      return (l.email || "").toLowerCase().includes(q)
        || (l.name || "").toLowerCase().includes(q)
        || (l.company || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const compute = (lead, key) => {
        if (key === "rmr") return (lead.sites || 0) * (lead.cameras || 0) * (lead.sell_price || 0);
        if (key === "earnings") return (lead.sites || 0) * (lead.cameras || 0) * ((lead.sell_price || 0) - DEALER_COST);
        let v = lead[key] ?? 0;
        return typeof v === "string" ? v.toLowerCase() : v;
      };
      const va = compute(a, sortKey);
      const vb = compute(b, sortKey);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ChevronDown size={11} color="#555" />;
    return sortDir === "asc" ? <ChevronUp size={11} color="#9e1c1c" /> : <ChevronDown size={11} color="#9e1c1c" />;
  };

  const Col = ({ label, k }) => (
    <th className="admin-th" onClick={() => toggleSort(k)} style={{ cursor: "pointer" }}>
      <span className="admin-th-inner">{label} <SortIcon k={k} /></span>
    </th>
  );

  return (
    <div className="admin-table-wrap">
      <div className="admin-table-toolbar">
        <div className="modal-input-wrap" style={{ maxWidth: 300, flex: 1 }}>
          <Mail size={13} className="modal-input-icon" />
          <input
            data-testid="leads-search"
            type="text"
            className="modal-input"
            placeholder="Search by name, company or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ fontSize: "0.85rem", padding: "10px 0" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="admin-lead-count" data-testid="lead-count">
            {filtered.length} lead{filtered.length !== 1 ? "s" : ""}
          </span>
          <button
            className="admin-export-btn"
            data-testid="export-csv-btn"
            onClick={() => exportCSV(leads)}
            title="Export all leads to CSV"
          >
            <Download size={13} />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      <div className="admin-table-scroll">
        <table className="admin-table" data-testid="leads-table">
          <thead>
            <tr>
              <Col label="Name" k="name" />
              <Col label="Company" k="company" />
              <Col label="Email" k="email" />
              <Col label="Sites" k="sites" />
              <Col label="Cams/Site" k="cameras" />
              <Col label="Sell Price" k="sell_price" />
              <Col label="Monthly RMR" k="rmr" />
              <Col label="Monthly Earnings" k="earnings" />
              <Col label="Date" k="timestamp" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="admin-td-empty">No leads found</td></tr>
            ) : filtered.map((lead, i) => {
              const totalCams = (lead.sites || 0) * (lead.cameras || 0);
              const rmr = totalCams * (lead.sell_price || 0);
              const earnings = totalCams * ((lead.sell_price || 0) - DEALER_COST);
              return (
                <tr key={lead.id || i} className="admin-tr" data-testid={`lead-row-${i}`}>
                  <td className="admin-td">{lead.name || "—"}</td>
                  <td className="admin-td">{lead.company || "—"}</td>
                  <td className="admin-td admin-td-email">{lead.email}</td>
                  <td className="admin-td admin-td-mono">{lead.sites ?? "—"}</td>
                  <td className="admin-td admin-td-mono">{lead.cameras ?? "—"}</td>
                  <td className="admin-td admin-td-mono">${lead.sell_price ?? "—"}</td>
                  <td className="admin-td admin-td-rmr">{fmtCurrency(rmr)}</td>
                  <td className="admin-td admin-td-earn">{fmtCurrency(earnings)}</td>
                  <td className="admin-td admin-td-date">{fmtDate(lead.timestamp)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Admin Dashboard ───────────────────────────────────────────────────────────
function AdminDashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showChangePw, setShowChangePw] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const totalRMR = leads.reduce((sum, l) => sum + (l.sites || 0) * (l.cameras || 0) * (l.sell_price || 0), 0);

  return (
    <div className="admin-dashboard">
      <div className="bg-texture" />

      {showChangePw && (
        <ChangePasswordModal token={token} onClose={() => setShowChangePw(false)} />
      )}

      {/* Nav */}
      <nav className="nav-bar">
        <div className="nav-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img src={LOGO_URL} alt="247 Alert" className="admin-nav-logo-img" />
            <span className="admin-nav-badge">Admin</span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button className="admin-refresh-btn" onClick={load} disabled={loading} data-testid="refresh-btn" title="Refresh">
              <RefreshCw size={14} className={loading ? "spin" : ""} />
            </button>
            <button className="admin-change-pw-btn" onClick={() => setShowChangePw(true)} data-testid="change-pw-btn">
              <KeyRound size={14} />
              <span>Change Password</span>
            </button>
            <button className="admin-logout-btn" onClick={onLogout} data-testid="logout-btn">
              <LogOut size={14} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="admin-content">
        <div className="admin-page-header">
          <div>
            <div className="hero-tag" style={{ marginBottom: 10 }}>Dealer Pipeline</div>
            <h1 className="admin-h1">Collected Leads</h1>
          </div>
          <a href="/" className="nav-cta" style={{ textDecoration: "none", alignSelf: "flex-end" }} data-testid="back-to-calc-btn">
            Back to Calculator
          </a>
        </div>

        {error && (
          <div className="admin-error-banner" data-testid="dashboard-error">
            <AlertCircle size={14} /><span>{error}</span>
          </div>
        )}

        <div className="admin-stats-grid" data-testid="stats-grid">
          <StatCard icon={Users}      label="Total Leads"    value={stats?.total_leads ?? "—"}                               sub="All time" />
          <StatCard icon={Calendar}   label="This Month"     value={stats?.this_month ?? "—"}                                sub="New leads" />
          <StatCard icon={TrendingUp} label="Pipeline RMR"   value={fmtCurrency(totalRMR)}                                  sub="Combined monthly" accent />
          <StatCard icon={DollarSign} label="Avg. Sell Price" value={stats?.avg_sell_price ? `$${stats.avg_sell_price}/cam` : "—"} sub="Per camera/month" />
          <StatCard icon={MapPin}     label="Avg. Sites"      value={stats?.avg_sites ?? "—"}                               sub="Per dealer" />
          <StatCard icon={Camera}     label="Avg. Cameras"    value={stats?.avg_cameras ?? "—"}                             sub="Per site" />
        </div>

        {loading ? (
          <div className="admin-loading" data-testid="loading-indicator">
            <RefreshCw size={20} className="spin" color="#9e1c1c" />
            <span>Loading leads...</span>
          </div>
        ) : (
          <LeadsTable leads={leads} />
        )}
      </div>
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
  };

  if (!token) return <AdminLogin onLogin={setToken} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
